<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUIStore } from '@/stores/uiStore'

/**
 * Two-slot drawer renderer for the app. Mounted ONCE by `default.vue`
 * inside `<main>` as an absolute-positioned overlay region. Renders
 * BOTH slots from `uiStore.drawer`:
 *
 *   - `right`  — slides in from the right edge. Width steps with the
 *                viewport: full on phone, 3/4 on tablet / iPad
 *                (`md:w-3/4`, a wide opaque overlay over un-pushed
 *                content), ~1/3 on desktop (`xl:w-1/3`, matches the
 *                `[2fr_1fr]` grid cell once the `xl` push seam
 *                engages). The seam is `xl` (1280px) so every iPad
 *                in portrait (≤1024) and most in landscape gets the
 *                roomy overlay, not the cramped 1/3 column. z-index 10.
 *   - `bottom` — slides up from the footer top; spans the full width
 *                of `<main>`; grows on its content height up to the
 *                header bottom border (capped via `max-h-full`).
 *                z-index 20 — bottom takes precedence over right
 *                when both are open.
 *
 * Both slots share the same `<DrawerCard>` chrome (each body
 * composes it). The host stays dumb: render `<component :is>`,
 * play the slide transition, dismiss on Esc / route change.
 *
 * ## Cross-cutting dismissal
 *
 *   - **Esc**: closes the topmost open slot (bottom first if open,
 *     else right). Two presses peel both off.
 *   - **Route change**: closes BOTH slots. Drawer survival across
 *     navigation is a flow leak (operator lands on a new view with
 *     a stale editor); per-slot consumers shouldn't have to remember.
 *
 * ## Geometry — bottom slot anchors
 *
 *   - `left-0` of `<main>`  = sidebar's right border (sidebar is a
 *                              sibling of `<main>`'s positioned
 *                              ancestor; `<main>` starts AFTER the
 *                              sidebar in the row).
 *   - `right-0` of `<main>` = viewport right edge.
 *   - `bottom-0` of `<main>` = footer top border (footer is OUTSIDE
 *                              `<main>` — the next sibling row).
 *   - `max-h-full` caps growth at `<main>`'s full height — i.e., the
 *                              header's bottom border.
 *   - DrawerCard's `flex-1 overflow-y-auto` middle slot handles
 *     overflow when content exceeds the cap.
 *
 * Slide transition: `translate-y-full → translate-y-0` on enter
 * (uses `<main>`'s `overflow: hidden` to clip the off-screen
 * portion). Same `duration-slower ease-panel` tokens as the right
 * slide so both feel coherent.
 */
const ui = useUIStore()
const route = useRoute()

const onKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return
  if (!ui.drawerOpen) return
  e.stopPropagation()
  // Topmost-first: bottom takes z precedence, peel it first.
  ui.closeDrawer()
}

// Auto-close on route change. The drawer host is mounted by the
// layout (not the route view), so without this watcher drawers
// would survive navigation — flow leak. Consumers should not have
// to remember to close them on nav. `route.fullPath` covers query +
// path changes; `flush: 'pre'` fires synchronously before the new
// route's body mounts so the close animation kicks off cleanly.
watch(
  () => route.fullPath,
  (_, prev) => {
    if (prev === undefined) return // initial subscription, not a nav
    ui.closeAllDrawers()
  },
)

onMounted(() => document.addEventListener('keydown', onKey))
onUnmounted(() => document.removeEventListener('keydown', onKey))

/* ── Bottom-drawer smooth height transitions ──────────────────────
 *
 * The animation itself is CSS — `transition: height ...` in the
 * universal `*` rule (`main.scss`). The browser does the
 * interpolation, timing, and easing.
 *
 * The unavoidable JS waypoint: CSS transitions only fire on
 * specified-value changes. `height: auto` resolving to different
 * lengths because content reflowed is layout recomputation — the
 * specified value is still `auto`, so no transition fires. This
 * hook writes the new natural extent as a concrete `style.height`
 * pixel value, turning the layout reflow into a specified-value
 * change the browser can animate.
 *
 * Two failure modes we're explicitly handling:
 *
 *   1. **v-show is invisible to a childList-only observer.** Group
 *      editor swaps Details/Members/Kiosks via `v-show` (inline
 *      `style="display: none"` toggles). Watching only `childList +
 *      subtree + characterData` misses them entirely — drawer never
 *      re-measures on section switch. Fix: also observe `attributes`
 *      filtered to `style` + `class`, and skip mutations whose target
 *      is the section itself (our own writes) so we don't loop.
 *
 *   2. **2-frame RAF dance creates perceptible delay.** Previous
 *      version used an outer RAF for debounce + inner RAF for the
 *      target write. ~32ms latency between content change and
 *      animation start. Fix: `queueMicrotask` debounce (end of
 *      current task, before next paint), no inner RAF. The browser
 *      compares end-of-task style state to prev-frame's; our
 *      temporary clear + measure + restore-as-target all happen
 *      within one microtask with no intermediate paint, so the
 *      transition fires cleanly from prevH → natural at the next
 *      frame boundary.
 */

const sectionBottomRef = ref<HTMLElement | null>(null)
let bottomMutObs: MutationObserver | null = null
let bottomMicrotaskPending = false

function syncBottomHeight(section: HTMLElement): void {
  const prevH = section.style.height
  // FLIP measurement: briefly release to read natural offsetHeight.
  // Suppress transition for the measure+restore so no animation
  // kicks off prematurely; we re-arm before the target write.
  section.style.transition = 'none'
  section.style.height = ''
  const natural = section.offsetHeight
  if (natural === 0 || `${natural}px` === prevH) {
    section.style.height = prevH
    section.style.transition = ''
    return
  }
  // Restore baseline + force reflow so the browser commits this
  // pixel value as the transition's start state for the next frame.
  section.style.height = prevH
  void section.offsetHeight
  // Hand transition control back to the cascade (the universal `*`
  // rule's `transition: height ...`).
  section.style.transition = ''
  // ⚠️ LOAD-BEARING — do NOT collapse this `requestAnimationFrame`
  // into the surrounding microtask.
  //
  // The forced reflow (`void offsetHeight`) above commits the
  // intermediate `height: ''` (auto-resolved) state mid-microtask.
  // Writing the target in the same microtask makes the transition
  // see `auto → natural` — and without `interpolate-size`, CSS
  // doesn't animate keyword ↔ length, so it snaps. The RAF moves
  // the target write to a separate frame so the restored `prevH` is
  // what the browser registers as the transition's start state.
  //
  // Two prior attempts that broke the animation:
  //  1) Collapsing this RAF into the outer microtask
  //     (`syncBottomHeight` doing everything inline) → height snapped.
  //  2) Using pure CSS (`interpolate-size: allow-keywords`) on the
  //     section → didn't fire because CSS only transitions
  //     specified-value changes, not layout-driven `auto ↔ auto`
  //     reflows.
  //
  // The current shape is the minimal-delay path: ~1 frame (16ms)
  // from mutation → animation start (microtask end-of-task + 1 RAF).
  // Pre-fix used 2 RAFs (~32ms).
  requestAnimationFrame(() => {
    if (sectionBottomRef.value) {
      sectionBottomRef.value.style.height = `${natural}px`
    }
  })
}

function scheduleBottomSync(): void {
  if (bottomMicrotaskPending) return
  bottomMicrotaskPending = true
  // Microtask (NOT RAF) for the debounce: runs at end of current
  // task before next paint. Combined with the inner RAF in
  // syncBottomHeight, total latency from mutation → animation start
  // is ~1 frame (16ms) instead of the original 2-frame RAF dance
  // (32ms).
  queueMicrotask(() => {
    bottomMicrotaskPending = false
    const section = sectionBottomRef.value
    if (section) syncBottomHeight(section)
  })
}

watch(
  () => ui.drawer.bottom.isOpen,
  async (open) => {
    if (open) {
      await nextTick()
      const section = sectionBottomRef.value
      if (!section) return
      // Seed initial height. Suppress transition just for the seed
      // so Vue's slide-up entrance (transform-only) isn't shadowed
      // by a 0 → natural height animation.
      section.style.transition = 'none'
      section.style.height = `${section.offsetHeight}px`
      void section.offsetHeight
      section.style.transition = ''

      bottomMutObs?.disconnect()
      bottomMutObs = new MutationObserver((mutations) => {
        // Filter our own style/class writes on the section itself.
        // Any mutation on a DESCENDANT (v-show display toggles,
        // class swaps, child inserts/removes, text changes) is what
        // we actually care about.
        const meaningful = mutations.some((m) => {
          if (m.target === section && m.type === 'attributes') return false
          return true
        })
        if (meaningful) scheduleBottomSync()
      })
      bottomMutObs.observe(section, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        // `style` catches v-show (inline display toggles);
        // `class` catches v-if-via-class and conditional class
        // swaps that might change layout.
        attributeFilter: ['style', 'class'],
      })
    } else {
      bottomMutObs?.disconnect()
      bottomMutObs = null
      // Don't touch styles on close — Vue's <Transition> leave is
      // running on transform; section unmounts shortly.
    }
  },
)

onUnmounted(() => {
  bottomMutObs?.disconnect()
})
</script>

<template>
  <!--
    Right slot — slides in from the right edge. z-10. Width is
    parent-relative and steps up below the desktop push seam:
    `w-full` phone → `md:w-3/4` tablet / iPad (wide opaque overlay)
    → `xl:w-1/3` desktop (matches the grid cell). `<main>`'s
    `overflow: hidden` clips the off-screen card. Default placement
    for back-compat.
  -->
  <Transition
    enter-active-class="transition-transform duration-slower ease-panel"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition-transform duration-slower ease-panel"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <section
      v-if="ui.drawer.right.isOpen"
      data-testid="drawer-right"
      role="dialog"
      aria-modal="false"
      class="absolute top-0 right-0 bottom-0 w-full md:w-3/4 xl:w-1/3 md:p-2 z-10 flex"
    >
      <component v-if="ui.drawer.right.body" :is="ui.drawer.right.body" />
    </section>
  </Transition>

  <!--
    Backdrop blur + dismiss layer — visible only while the bottom
    drawer is open. Fills `<main>` and applies
    `backdrop-filter: blur()` so EVERYTHING under it (route content,
    right drawer if open) reads as out-of-focus while the bottom
    drawer is the active context. Bottom drawer itself is opaque +
    sits at z-20, so the band the drawer covers is fully obscured;
    the strip ABOVE the drawer (the rest of `<main>`) shows the
    blurred content through this layer.

    z-15 places the layer between the right drawer (z-10) and the
    bottom drawer (z-20) so right gets blurred too — matches the
    "bottom takes precedence" intent.

    The layer is interactive (NOT `pointer-events-none`):
      - Blocks click-through so the operator can't accidentally
        trigger underlying route content while the bottom drawer
        is the active focus.
      - Click anywhere ON the backdrop dismisses the bottom drawer
        (modal-style scrim semantics). Clicks on the bottom drawer
        itself land on z-20 first and never reach this layer.

    Fade in/out on the same panel timing tokens so it feels of-a-
    piece with the slide.
  -->
  <Transition
    enter-active-class="transition-opacity duration-slower ease-panel"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-slower ease-panel"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="ui.drawer.bottom.isOpen"
      class="absolute inset-0 z-[15] backdrop-blur-md cursor-pointer"
      aria-hidden="true"
      @click="ui.closeDrawer('bottom')"
    />
  </Transition>

  <!--
    Bottom slot — slides up from the footer top edge. Spans full
    `<main>` width (sidebar right → viewport right). Capped at
    main's full height (header bottom). z-20 — bottom takes
    precedence over right when both are open. NO host-side padding
    (anchors flush to footer-top + sidebar-right per the spec); the
    body's own DrawerCard handles the visual envelope (opaque
    surface, top-corners-only rounding for `placement="bottom"`).
  -->
  <Transition
    enter-active-class="transition-transform duration-slower ease-panel"
    enter-from-class="translate-y-full"
    enter-to-class="translate-y-0"
    leave-active-class="transition-transform duration-slower ease-panel"
    leave-from-class="translate-y-0"
    leave-to-class="translate-y-full"
  >
    <section
      v-if="ui.drawer.bottom.isOpen"
      ref="sectionBottomRef"
      data-testid="drawer-bottom"
      role="dialog"
      aria-modal="false"
      class="absolute left-0 right-0 bottom-0 max-h-full z-20 flex"
    >
      <component v-if="ui.drawer.bottom.body" :is="ui.drawer.bottom.body" />
    </section>
  </Transition>
</template>

